import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { X, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FileItem {
  file: File;
  preview: string;
}

interface FileUploadProps {
  name: string;
  maxFiles?: number;
  maxSize?: number;
  accept?: Record<string, string[]>;
}

export const FileUpload = ({
  name,
  maxFiles = 3,
  maxSize = 50 * 1024 * 1024,
  accept = {
    "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    "video/*": [".mp4", ".webm", ".ogg"],
    "audio/*": [".mp3"],
  },
}: FileUploadProps) => {
  const { setValue, watch } = useFormContext();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles: FileItem[] = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      setValue(
        name,
        [...files, ...newFiles].slice(0, maxFiles).map((f) => f.file),
      );
      setFiles((prevFiles) => [...prevFiles, ...newFiles].slice(0, maxFiles));
      console.log(watch());
    },
    [setValue, name, files, maxFiles, watch],
  );

  const removeFile = (index: number) => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      setValue(
        name,
        newFiles.map((f) => f.file),
      );
      return newFiles;
    });
    if (currentIndex >= files.length - 1 && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: maxFiles - files.length,
    maxSize,
    accept,
  });

  const showNextImage = () => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const showPreviousImage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  return (
    <div className="testify-space-y-4">
      <div
        {...getRootProps()}
        className={`testify-border-2 testify-border-dashed testify-rounded-lg testify-p-6 testify-text-center testify-cursor-pointer testify-transition ${
          isDragActive
            ? "testify-border-primary/50 testify-bg-primary/10"
            : "testify-border-gray-300 testify-hover:border-primary/50"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="testify-mx-auto testify-h-8 testify-w-8 testify-text-gray-400" />
        <p className="testify-mt-2 testify-text-sm testify-text-gray-600">
          Drop your files here, or{" "}
          <span className="testify-text-primary testify-font-medium">
            browse
          </span>
        </p>
        <p className="testify-mt-1 testify-text-xs testify-text-gray-500">
          Max {maxFiles} file{maxFiles > 1 ? "s" : ""}, up to{" "}
          {maxSize / 1024 / 1024}MB each
        </p>
      </div>

      {files.length > 0 && (
        <div className="testify-space-y-4">
          <div className="testify-relative testify-aspect-video testify-rounded-lg testify-overflow-hidden testify-bg-gray-100">
            {files[currentIndex].file.type.startsWith("image/") ? (
              <img
                src={files[currentIndex].preview}
                alt={files[currentIndex].file.name}
                className="testify-object-contain testify-w-full testify-h-full testify-flex-shrink-0"
              />
            ) : (
              <video
                src={files[currentIndex].preview}
                className="testify-object-contain testify-w-full testify-h-full"
                controls
              />
            )}
            <Button
              variant="destructive"
              size="icon"
              className="testify-absolute testify-top-2 testify-right-2 testify-rounded-full"
              onClick={() => removeFile(currentIndex)}
            >
              <X className="testify-h-4 testify-w-4" />
            </Button>
          </div>

          <div className="testify-flex testify-justify-between testify-items-center">
            <Button
              variant="outline"
              size="icon"
              onClick={showPreviousImage}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="testify-h-4 testify-w-4" />
            </Button>
            <div className="testify-text-sm testify-text-gray-500">
              {currentIndex + 1} of {files.length}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={showNextImage}
              disabled={currentIndex === files.length - 1}
            >
              <ChevronRight className="testify-h-4 testify-w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
